import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SITE } from "../lib/site";
import { Menu, X } from "lucide-react";

const links = [
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#gallery", label: "Gallery" },
  { href: "#reviews", label: "Reviews" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#F5F5F0]/85 backdrop-blur-md border-b border-[#E5E5E0]"
          : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#top" className="flex items-baseline gap-2 group" data-testid="navbar-logo">
          <span className="font-serif-display text-3xl font-medium tracking-tighter text-[#1C1C1A]">
            JSE
          </span>
          <span className="font-sans-body text-[11px] uppercase tracking-[0.25em] text-[#4A4A46] hidden sm:inline">
            Painting · Fort Worth
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-sans-body text-sm text-[#1C1C1A] hover:text-[#CC5500] transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href="#estimate"
          data-testid="navbar-cta-estimate"
          className="hidden md:inline-flex items-center gap-2 bg-[#1C1C1A] text-[#F5F5F0] hover:bg-[#CC5500] transition-colors duration-300 px-5 py-2.5 text-sm font-sans-body uppercase tracking-[0.15em]"
        >
          Free Estimate
        </a>

        <button
          onClick={() => setOpen(!open)}
          className="md:hidden text-[#1C1C1A] p-2"
          data-testid="navbar-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#F5F5F0] border-t border-[#E5E5E0]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="font-sans-body text-base text-[#1C1C1A]"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#estimate"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-estimate"
                className="bg-[#1C1C1A] text-[#F5F5F0] px-5 py-3 text-sm uppercase tracking-[0.15em] text-center"
              >
                Free Estimate
              </a>
              <a
                href={SITE.phoneHref}
                className="font-sans-body text-sm text-[#4A4A46]"
              >
                Call {SITE.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
